import { Box, Tooltip as MuiTooltip, Typography } from "@mui/material";
import { type ReactNode, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartTooltipConfig } from "src/ui/widgets/charts";
import { createCurrencyFormatter } from "utils/currency-utils";
import { ChartContainer } from "../../../../widgets/charts";

export interface ChartSeries {
  dataKey: string;
  label: string;
  fill: string;
  color?: string;
  radius?: [number, number, number, number];
  stackId?: string;
  barSize?: number;
  tooltip: ReactNode;
}

export interface DashboardUnifiedChartProps<T extends Record<string, any>> {
  title?: string;
  titleTooltip?: ReactNode;
  data: T[];
  series: ChartSeries[];
  tooltip?: ChartTooltipConfig;
  barCategoryGap?: string;
  xAxisKey?: string;
  xAxisFormatter?: (value: string) => string;
}

const defaultCurrencyFormatter = createCurrencyFormatter("GBP")({
  rounding: {
    kind: "rounded",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
});

const defaultLabelFormatter = (dataKey: string): string => {
  // Convert camelCase to Title Case
  return dataKey
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

export const DashboardUnifiedChart = <T extends Record<string, any>>({
  title,
  titleTooltip,
  data,
  series,
  tooltip = {},
  barCategoryGap = "60%",
  xAxisKey = "month",
  xAxisFormatter = (value) => value,
}: DashboardUnifiedChartProps<T>) => {
  // Derive bars and legends from series
  const bars = series.map((s) => ({
    dataKey: s.dataKey,
    fill: s.fill,
    radius: s.radius,
    stackId: s.stackId,
    barSize: s.barSize,
  }));

  // For non-stacked bars, reverse the order so first item renders on top
  const hasStackId = series.some((s) => s.stackId);
  const orderedBars = hasStackId ? bars : bars.reverse();

  const legends = series.map((s) => ({
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || s.fill,
    tooltip: s.tooltip,
  }));

  // NB: This is to avoid the chart from scaling tiny values to full height when there are no values available
  // Calculate the maximum value from all data points to establish proper scale
  const maxDataValue = useMemo(() => {
    let max = 0;

    // Get all numeric dataKeys from bars
    const numericKeys = orderedBars.map((bar) => bar.dataKey);

    data.forEach((item) => {
      numericKeys.forEach((key) => {
        const value = item[key];
        if (typeof value === "number" && value > max) {
          max = value;
        }
      });
    });

    // If all values are null/undefined or no data, use a reasonable default scale
    // This prevents the chart from scaling tiny values to full height
    return max > 0 ? max * 1.1 : 100; // Add 10% padding to max or use 100 as default
  }, [data, orderedBars]);

  // Transform data for recharts, handling missing data points
  const chartData = useMemo(() => {
    return data.map((item) => {
      const transformedItem: Record<string, any> = {
        [xAxisKey]: item[xAxisKey],
      };

      // Check if this is a placeholder (month with no actual data)
      const hasActualData = orderedBars.some(
        (bar) =>
          Object.hasOwn(item, bar.dataKey) && item[bar.dataKey] !== undefined,
      );

      orderedBars.forEach((bar) => {
        if (hasActualData && item[bar.dataKey] !== undefined) {
          // Real data point
          transformedItem[bar.dataKey] = item[bar.dataKey];
          transformedItem[`${bar.dataKey}_isNull`] = false;
        } else {
          // Missing data point - use tiny value for tooltip visibility
          transformedItem[bar.dataKey] = maxDataValue * 0.001; // 0.1% of max height
          transformedItem[`${bar.dataKey}_isNull`] = true;
        }
      });

      return transformedItem;
    });
  }, [data, orderedBars, xAxisKey, maxDataValue]);

  const {
    labelFormatter = defaultLabelFormatter,
    valueFormatter = defaultCurrencyFormatter.format,
  } = tooltip;

  // Custom tick formatter for X-axis
  const formatXAxisTick = (value: string) => {
    return xAxisFormatter(value);
  };

  // Custom tooltip component that handles null values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Get the original data point to check for null flags
      const dataPoint = chartData.find((d) => d[xAxisKey] === label);

      // Check if all actual data values (not the null flags) are null
      const actualDataPayload = payload.filter(
        (entry: any) => !entry.dataKey.includes("_isNull"),
      );
      const allValuesNull = actualDataPayload.every((entry: any) => {
        return dataPoint && dataPoint[`${entry.dataKey}_isNull`] === true;
      });

      if (allValuesNull) {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: 1,
              padding: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                fontWeight: 500,
                fontStyle: "italic",
              }}
            >
              No data
            </Typography>
          </Box>
        );
      }

      // Create a map of dataKey to legend order
      const legendOrder = new Map(
        legends.map((legend, index) => [legend.dataKey, index]),
      );

      // Create a map of dataKey to label
      const legendLabels = new Map(
        legends.map((legend) => [legend.dataKey, legend.label]),
      );

      // Sort actualDataPayload based on legend order
      const sortedPayload = [...actualDataPayload].sort((a, b) => {
        const orderA = legendOrder.get(a.dataKey) ?? Number.MAX_VALUE;
        const orderB = legendOrder.get(b.dataKey) ?? Number.MAX_VALUE;
        return orderA - orderB;
      });

      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            padding: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {sortedPayload.map((entry: any, index: number) => {
            // Check if this specific value was null
            const isNull =
              dataPoint && dataPoint[`${entry.dataKey}_isNull`] === true;

            if (isNull) {
              return null;
            }

            // Get the label from the legends configuration, fallback to labelFormatter if not found
            const label =
              legendLabels.get(entry.dataKey) || labelFormatter(entry.dataKey);

            return (
              <Typography
                key={entry.dataKey}
                variant="body2"
                sx={{
                  color: "#666",
                  fontWeight: 500,
                  mb: index === sortedPayload.length - 1 ? 0 : 0.5,
                }}
              >
                {label} {valueFormatter(entry.value)}
              </Typography>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  // Tooltip component props for consistent styling
  const tooltipProps = {
    arrow: true,
    placement: "top" as const,
    componentsProps: {
      tooltip: {
        sx: {
          bgcolor: "white",
          color: "#000",
          border: "1px solid #ccc",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "& .MuiTooltip-arrow": {
            color: "white",
            "&::before": {
              border: "1px solid #ccc",
            },
          },
        },
      },
    },
  };

  return (
    <ChartContainer title={title || ""} titleTooltip={titleTooltip}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 20,
                left: 20,
                bottom: 10,
              }}
              barCategoryGap={barCategoryGap}
            >
              <CartesianGrid strokeDasharray="none" stroke="transparent" />
              <XAxis
                dataKey={xAxisKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 14, fill: "#666", fontWeight: 500 }}
                tickFormatter={formatXAxisTick}
              />
              <YAxis hide domain={[0, maxDataValue]} />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
              />
              {orderedBars.map((bar) => (
                <Bar
                  key={bar.dataKey}
                  dataKey={bar.dataKey}
                  fill={bar.fill}
                  radius={bar.radius}
                  stackId={bar.stackId}
                  barSize={bar.barSize}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}-${entry[xAxisKey]}`}
                      fill={
                        entry[`${bar.dataKey}_isNull`]
                          ? "transparent"
                          : bar.fill
                      }
                      stroke={
                        entry[`${bar.dataKey}_isNull`] ? "#e0e0e0" : "none"
                      }
                      strokeWidth={entry[`${bar.dataKey}_isNull`] ? 1 : 0}
                      strokeDasharray={
                        entry[`${bar.dataKey}_isNull`] ? "3 3" : "none"
                      }
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Bottom legend with colored dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            pt: 2,
            pb: 1,
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {legends.map((legend) => (
            <MuiTooltip
              key={legend.dataKey}
              title={legend.tooltip}
              {...tooltipProps}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: legend.color,
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  {legend.label}
                </Typography>
              </Box>
            </MuiTooltip>
          ))}
        </Box>
      </Box>
    </ChartContainer>
  );
};
